// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import loadable from '@loadable/component';
import * as React from 'react';
import { Route, type RouteObject, Routes } from 'react-router';
import AppShell from './AppShell';
import PageLoading from './components/common/loading/PageLoading';
import Maintenance from './components/maintenance/Maintenance';
import type { AppContextType } from './context/AppContext';
import { Error404 } from './lib/error';
import * as routes from './routes';

const Dashboard = loadable(() => import(/* webpackPrefetch: true */ './pages/Dashboard'), {
  fallback: <PageLoading />,
});
const Harvests = loadable(() => import(/* webpackPrefetch: true */ './pages/Harvests'), {
  fallback: <PageLoading />,
});
const Orders = loadable(() => import(/* webpackPrefetch: true */ './pages/Orders'), {
  fallback: <PageLoading />,
});
const Tasks = loadable(() => import(/* webpackPrefetch: true */ './pages/Tasks'), {
  fallback: <PageLoading />,
});
const RecurringOrders = loadable(() => import(/* webpackPrefetch: true */ './pages/RecurringOrders'), {
  fallback: <PageLoading />,
});
const Crops = loadable(() => import(/* webpackPrefetch: true */ './pages/Crops'), {
  fallback: <PageLoading />,
});
const PlantingGroups = loadable(() => import(/* webpackPrefetch: true */ './pages/Plantings'), {
  fallback: <PageLoading />,
});
const Calendar = loadable(() => import(/* webpackPrefetch: true */ './pages/Calendar'), {
  fallback: <PageLoading />,
});
const HarvestView = loadable(() => import('./pages/HarvestView'), {
  fallback: <PageLoading />,
});
export const preloadHarvestView = () => HarvestView.preload();
const CropView = loadable(() => import('./pages/CropView'), {
  fallback: <PageLoading />,
});
export const preloadCropView = () => CropView.preload();
const BlendView = loadable(() => import('./pages/BlendView'), {
  fallback: <PageLoading />,
});
export const preloadBlendView = () => BlendView.preload();
const RecurringOrderView = loadable(() => import('./pages/RecurringOrderView'), {
  fallback: <PageLoading />,
});
export const preloadRecurringOrderView = () => RecurringOrderView.preload();
const RecurringTaskView = loadable(() => import('./pages/RecurringTaskView'), {
  fallback: <PageLoading />,
});
export const preloadRecurringTaskView = () => RecurringTaskView.preload();
const OrderView = loadable(() => import('./pages/OrderView'), {
  fallback: <PageLoading />,
});
export const preloadOrderView = () => OrderView.preload();
const TaskView = loadable(() => import('./pages/TaskView'), {
  fallback: <PageLoading />,
});
export const preloadTaskView = () => TaskView.preload();
const TaskGroupView = loadable(() => import('./pages/TaskGroupView'), {
  fallback: <PageLoading />,
});
export const preloadTaskGroupView = () => TaskGroupView.preload();
const PlantingView = loadable(() => import('./pages/PlantingView'), {
  fallback: <PageLoading />,
});
export const preloadPlantingView = () => PlantingView.preload();
const PlantingGroupView = loadable(() => import('./pages/PlantingGroupView'), {
  fallback: <PageLoading />,
});
export const preloadPlantingGroupView = () => PlantingGroupView.preload();
const BlogPost = loadable(() => import('./pages/BlogPost'), {
  fallback: <PageLoading />,
});
const Surpluses = loadable(() => import('./pages/Surpluses'), {
  fallback: <PageLoading />,
});
const RecurringTasks = loadable(() => import('./pages/RecurringTasks'), {
  fallback: <PageLoading />,
});
const Account = loadable(() => import('./pages/Account'), {
  fallback: <PageLoading />,
});
const FarmReport = loadable(() => import('./pages/FarmReport'), {
  fallback: <PageLoading />,
});
const Blends = loadable(() => import('./pages/Blends'), {
  fallback: <PageLoading />,
});
const Home = loadable(() => import('./pages/Home'), {
  fallback: <PageLoading />,
});
const Blog = loadable(() => import('./pages/Blog'), {
  fallback: <PageLoading />,
});
const Contact = loadable(() => import('./pages/Contact'), {
  fallback: <PageLoading />,
});
const FAQ = loadable(() => import('./pages/FAQ'), {
  fallback: <PageLoading />,
});
const PasswordReset = loadable(() => import('./pages/PasswordReset'), {
  fallback: <PageLoading />,
});
const Pricing = loadable(() => import('./pages/Pricing'), {
  fallback: <PageLoading />,
});
const Privacy = loadable(() => import('./pages/Privacy'), {
  fallback: <PageLoading />,
});
const SendPasswordReset = loadable(() => import('./pages/SendPasswordReset'), {
  fallback: <PageLoading />,
});
const SignIn = loadable(() => import('./pages/SignIn'), {
  fallback: <PageLoading />,
});
const SignUp = loadable(() => import('./pages/SignUp'), {
  fallback: <PageLoading />,
});
const SignUpLink = loadable(() => import('./pages/SignUpLink'), {
  fallback: <PageLoading />,
});
const Team = loadable(() => import('./pages/Team'), {
  fallback: <PageLoading />,
});
const Terms = loadable(() => import('./pages/Terms'), {
  fallback: <PageLoading />,
});
const TrayCalculator = loadable(() => import('./pages/TrayCalculator'), {
  fallback: <PageLoading />,
});
const SeedDensityCalculator = loadable(() => import('./pages/SeedDensityCalculator'), {
  fallback: <PageLoading />,
});
const PriceCalculator = loadable(() => import('./pages/PriceCalculator'), {
  fallback: <PageLoading />,
});
const VerifyEmail = loadable(() => import('./pages/VerifyEmail'), {
  fallback: <PageLoading />,
});
const ExcelTool = loadable(() => import('./pages/ExcelTool'), {
  fallback: <PageLoading />,
});

export const ROUTE_CONFIGS: RouteObject[] = [
  {
    path: routes.HOME,
    element: Home,
  },
  {
    path: routes.DASHBOARD,
    element: Dashboard,
  },
  {
    path: routes.ORDERS,
    element: Orders,
  },
  {
    path: routes.RECURRING_ORDERS,
    element: RecurringOrders,
  },
  {
    path: routes.HARVESTS,
    element: Harvests,
  },
  {
    path: routes.TASKS,
    element: Tasks,
  },
  {
    path: routes.RECURRING_TASKS,
    element: RecurringTasks,
  },
  {
    path: routes.CROPS,
    element: Crops,
  },
  {
    path: routes.BLENDS,
    element: Blends,
  },
  {
    path: routes.CALENDAR,
    element: Calendar,
  },
  {
    path: routes.makeCropIdPath(),
    element: CropView,
  },
  {
    path: routes.makeOrderIdPath(),
    element: OrderView,
  },
  {
    path: routes.makeRecurringOrderIdPath(),
    element: RecurringOrderView,
  },
  {
    path: routes.makeRecurringTaskIdPath(),
    element: RecurringTaskView,
  },
  {
    path: routes.makePlantingIdPath(),
    element: PlantingView,
  },
  {
    path: routes.makeHarvestIdPath(),
    element: HarvestView,
  },
  {
    path: routes.makeTaskIdPath(),
    element: TaskView,
  },
  {
    path: routes.makeTaskGroupIdPath(),
    element: TaskGroupView,
  },
  {
    path: routes.makePlantingGroupIdPath(),
    element: PlantingGroupView,
  },
  {
    path: routes.makeBlendIdPath(),
    element: BlendView,
  },
  {
    path: routes.PLANTINGS,
    element: PlantingGroups,
  },
  {
    path: routes.BLOG,
    element: Blog,
  },
  {
    path: routes.makeBlogTitlePath(),
    element: BlogPost,
  },
  {
    path: routes.FARM_REPORT,
    element: FarmReport,
  },
  {
    path: routes.ACCOUNT,
    element: Account,
  },
  {
    path: routes.PRICING,
    element: Pricing,
  },
  {
    path: routes.TUTORIALS,
    element: FAQ,
  },
  {
    path: routes.TEAM,
    element: Team,
  },
  {
    path: routes.PRIVACY,
    element: Privacy,
  },
  {
    path: routes.TERMS,
    element: Terms,
  },
  {
    path: routes.CONTACT,
    element: Contact,
  },
  {
    path: routes.SIGNUP,
    element: SignUp,
  },
  {
    path: routes.VERIFY_EMAIL,
    element: VerifyEmail,
  },
  {
    path: routes.SIGNIN,
    element: SignIn,
  },
  {
    path: routes.SIGNUP_LINK,
    element: SignUpLink,
  },
  {
    path: routes.PASSWORD_RESET,
    element: PasswordReset,
  },
  {
    path: routes.FORGOT_PASSWORD,
    element: SendPasswordReset,
  },
  {
    path: routes.SURPLUSES_BUFFERS,
    element: Surpluses,
  },
  {
    path: routes.TRAY_CALCULATOR,
    element: TrayCalculator,
  },
  {
    path: routes.PRICE_CALCULATOR,
    element: PriceCalculator,
  },
  {
    path: routes.SEED_DENSITY_CALCULATOR,
    element: SeedDensityCalculator,
  },
  {
    path: routes.EXCEL_TOOL,
    element: ExcelTool,
  },
  {
    path: '*',
    element: Error404,
  },
];

interface ExternalProps {
  readonly appContext: AppContextType;
}

export default class App extends React.Component<ExternalProps> {
  public render() {
    return (
      <AppShell appContext={this.props.appContext}>
        {this.props.appContext.options.maintenance ? (
          <Maintenance
            maintenanceDurationMinutes={this.props.appContext.options.maintenanceDurationMinutes}
            maintenanceStartTime={this.props.appContext.options.maintenanceStartTime}
          />
        ) : (
          <Routes>
            {ROUTE_CONFIGS.map(({ path, element }) => {
              // TODO: fix this
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              const Element = element as any;
              return <Route key={path} path={path} element={<Element />} />;
            })}
          </Routes>
        )}
      </AppShell>
    );
  }
}
